/**
 * Utilidad para obtener información de servicio (customer, contract, service)
 * con deduplicación, caché en memoria y retry ante rate limit (HTTP 429).
 *
 * Usa el proxy del backend (misma cookie de sesión que el Dashboard):
 * GET /api/client-proxy/service_types/service-info/{contract_id}/{service_id}
 * El Flask reenvía a BACKEND_CLIENT_API_BASE_URL (prep-client-microservice, etc.).
 */

import { getApiUrl } from '../config/api';

const DEFAULT_FALLBACK = {
  customer: { name: 'N/A' },
  contract: { name: 'N/A' },
  service: { name: 'N/A' }
};

/** Algunos proxies devuelven `{ data: { customer, contract, service } }`. */
function unwrapServiceInfoBody(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const inner = payload.data;
  if (inner && typeof inner === 'object' && (inner.customer != null || inner.contract != null || inner.service != null)) {
    return inner;
  }
  return payload;
}

/**
 * Misma prioridad que _normalize_client_service_info_payload en task_service.py
 * (MS puede enviar version, Version, contract_version o amount_link_name).
 */
function pickContractVersionCanonical(merged, details) {
  const keys = ['version', 'Version', 'contract_version'];
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = merged[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  if (merged.amount_link_name != null && String(merged.amount_link_name).trim() !== '') {
    return String(merged.amount_link_name).trim();
  }
  const rootKeys = ['version', 'Version'];
  for (let i = 0; i < rootKeys.length; i += 1) {
    const k = rootKeys[i];
    const v = details && details[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

/**
 * Mantiene el `contract` tal cual viene del MS y fija `version` en clave canónica minúscula
 * (no reescribe `name`).
 */
function mergeContractVersionFromDetails(details) {
  const raw = details && details.contract != null ? details.contract : null;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw || DEFAULT_FALLBACK.contract;
  }
  const merged = { ...raw };
  const vFinal = pickContractVersionCanonical(merged, details || {});
  if (vFinal != null) merged.version = vFinal;
  return merged;
}

/** Texto para «Código del contrato» (Dashboard ticketMeta, etc.). */
export function getContractCodeLabel(ticket) {
  const c = ticket?.contract;
  if (!c || typeof c !== 'object') return '';
  const v = pickContractVersionCanonical({ ...c }, {});
  return v || '';
}

/** Tiempo de vida de la caché en memoria (evita recargas al cambiar tab / refrescar listas). */
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min

/** @type {Map<string, { value: object, expiresAt: number }>} */
const serviceInfoMemoryCache = new Map();

/** @type {Map<string, Promise<object>>} */
const pendingServiceInfoFetches = new Map();

function cacheKey(contractId, serviceId) {
  return `${contractId}|${serviceId}`;
}

/**
 * Limpia caché y peticiones pendientes (p. ej. al cerrar sesión).
 */
export function clearServiceInfoCache() {
  serviceInfoMemoryCache.clear();
  pendingServiceInfoFetches.clear();
}

function getCachedDetails(key) {
  const entry = serviceInfoMemoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    serviceInfoMemoryCache.delete(key);
    return null;
  }
  return entry.value;
}

/**
 * Obtiene detalles de servicio con reintento ante 429.
 * Resultados correctos se guardan en caché; fallos no se cachean para permitir reintentos.
 * @param {number|string} contract_id
 * @param {number|string} serviceId
 * @param {number} retries - intentos restantes
 * @returns {Promise<{customer, contract, service, frequency?}>}
 */
async function fetchServiceInfoFromNetwork(contractId, serviceId, retries = 2) {
  const url = getApiUrl('SERVICE_INFO_PROXY', contractId, serviceId);
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });

    if (response.status === 429 && retries > 0) {
      const delayMs = 800 * (3 - retries);
      await new Promise(r => setTimeout(r, delayMs));
      return fetchServiceInfoFromNetwork(contractId, serviceId, retries - 1);
    }

    if (!response.ok) return null;
    const body = await response.json().catch(() => null);
    if (!body || typeof body !== 'object') return null;
    return unwrapServiceInfoBody(body);
  } catch (error) {
    if (retries > 0) {
      await new Promise(r => setTimeout(r, 500));
      return fetchServiceInfoFromNetwork(contractId, serviceId, retries - 1);
    }
    return null;
  }
}

/**
 * Obtiene detalles de servicio: caché → petición en curso → red.
 */
export async function fetchServiceInfo(contractId, serviceId) {
  if (!serviceId) return DEFAULT_FALLBACK;

  if (contractId == null || contractId === '') return DEFAULT_FALLBACK;
  const key = cacheKey(contractId, serviceId);

  const cached = getCachedDetails(key);
  if (cached) return cached;

  const existing = pendingServiceInfoFetches.get(key);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const details = await fetchServiceInfoFromNetwork(contractId, serviceId);
      // No cachear fallos: evita 10 min de N/A si hubo 401/502 o MS caído.
      if (details && typeof details === 'object') {
        serviceInfoMemoryCache.set(key, {
          value: details,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
      }
      return details || DEFAULT_FALLBACK;
    } finally {
      pendingServiceInfoFetches.delete(key);
    }
  })();

  pendingServiceInfoFetches.set(key, promise);
  return promise;
}

/**
 * Enriquece tickets con datos de cliente/contrato/servicio.
 * Deduplica por (contract_id, service_id) y reutiliza caché entre cargas.
 * @param {Array} tickets - lista de tickets con contract_id, service_id
 * @returns {Promise<Array>} tickets enriquecidos
 */
export async function enrichTicketsWithServiceInfo(tickets) {
  if (!tickets || tickets.length === 0) return tickets;

  const uniqueKeys = new Set();
  const ticketsNeedingData = [];
  tickets.forEach((t) => {
    const contractId = t.contract_id;
    if (t.service_id && contractId != null) {
      const key = cacheKey(contractId, t.service_id);
      if (!uniqueKeys.has(key)) {
        uniqueKeys.add(key);
        ticketsNeedingData.push({ contractId, service_id: t.service_id, key });
      }
    }
  });

  const detailsMap = {};

  const toFetch = [];
  for (const item of ticketsNeedingData) {
    const fromCache = getCachedDetails(item.key);
    if (fromCache) {
      detailsMap[item.key] = fromCache;
    } else {
      toFetch.push(item);
    }
  }

  const BATCH_SIZE = 4;
  for (let i = 0; i < toFetch.length; i += BATCH_SIZE) {
    if (i > 0) await new Promise(r => setTimeout(r, 150));
    const batch = toFetch.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async ({ contractId, service_id, key }) => {
        const details = await fetchServiceInfo(contractId, service_id);
        return { key, details };
      })
    );
    results.forEach(({ key, details }) => {
      detailsMap[key] = details;
    });
  }

  return tickets.map((ticket) => {
    const contractId = ticket.contract_id;
    if (!ticket.service_id || contractId == null) {
      return { ...ticket, ...DEFAULT_FALLBACK };
    }
    const key = cacheKey(contractId, ticket.service_id);
    const details = detailsMap[key] || DEFAULT_FALLBACK;
    const payload = unwrapServiceInfoBody(details);
    return {
      ...ticket,
      customer: payload.customer || DEFAULT_FALLBACK.customer,
      contract: mergeContractVersionFromDetails(payload),
      service: payload.service || DEFAULT_FALLBACK.service,
      frequency: payload.frequency != null ? payload.frequency : ticket.frequency
    };
  });
}
